import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getHasPendingOrCompleteCertificate } from '@/redux/modules/certCapture';
import { getTaxStatus } from '@/redux/modules/taxes';
import { OtherCharge, Payment } from '@/types/Invoice';
import { PaymentMethods, PaymentProviders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import InvoiceSummaryFeesList from './InvoiceSummaryFeesList';
import InvoiceSummaryList from './components/InvoiceSummaryList';
import InvoiceSummaryListRow from './components/InvoiceSummaryListRow';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import RowDivider from '../RowDivider';
import styled from 'styled-components';

type Props = {
    activeProcessingFee: number; //cents
    bidderPaysProcessingFee: boolean;
    buyersPremium: number;
    catalogId: number;
    currency: string;
    hideShipping?: boolean;
    houseShippingQuote: number;
    installmentFee: number;
    invoiceId: number;
    invoiceTotal: number;
    itemTotal: number;
    otherCharges: OtherCharge[];
    paymentMethod: PaymentMethods;
    paymentProvider: PaymentProviders;
    payments: Payment[];
    readOnly: boolean;
    salesTax: number;
    shipping: number;
};

const InvoiceSummaryCharges = ({
    activeProcessingFee,
    bidderPaysProcessingFee,
    buyersPremium,
    catalogId,
    currency = 'USD',
    hideShipping = false,
    houseShippingQuote = 0,
    invoiceId,
    invoiceTotal,
    itemTotal,
    otherCharges = [],
    paymentMethod,
    paymentProvider,
    payments = [],
    readOnly = false,
    salesTax = 0,
    shipping = 0,
}: Props) => {
    const InAvaTaxBeta = useAppSelector((state) => getTaxStatus(state, invoiceId));
    const certInPlay = useAppSelector(getHasPendingOrCompleteCertificate);

    return (
        <InvoiceSummaryList>
            <StyledRowDividerTop />
            <InvoiceSummaryListRow
                amount={{ amount: itemTotal, currency }}
                label={<FormattedMessage id="checkout.invoice_summary.items_total" />}
            />
            <InvoiceSummaryListRow
                amount={{ amount: buyersPremium, currency }}
                label={<FormattedMessage id="checkout.invoice_summary.buyers_premium" />}
            />
            <InvoiceSummaryFeesList
                activeProcessingFee={activeProcessingFee}
                bidderPaysProcessingFee={bidderPaysProcessingFee}
                catalogId={catalogId}
                currency={currency}
                otherCharges={otherCharges}
                paymentMethod={paymentMethod}
                paymentProvider={paymentProvider}
                readOnly={readOnly}
            />
            {!hideShipping && (
                <InvoiceSummaryListRow
                    amount={{
                        amount: shipping || houseShippingQuote,
                        currency,
                    }}
                    label={<FormattedMessage id="checkout.invoice_summary.shipping" />}
                />
            )}
            <InvoiceSummaryListRow
                amount={{ amount: salesTax, currency }}
                infoText={
                    InAvaTaxBeta && (
                        <>
                            <FormattedMessage id="checkout.invoice_summary.tax_popover" />
                            <div>
                                <Link
                                    rel="noopener"
                                    target="_blank"
                                    to="/dashboard/profile?tab=tax-cert-management"
                                >
                                    <FormattedMessage
                                        id={
                                            certInPlay
                                                ? 'checkout.invoice_summary.view_tax_exempt'
                                                : 'checkout.invoice_summary.apply_tax_exempt'
                                        }
                                    />
                                </Link>
                            </div>
                        </>
                    )
                }
                label={<FormattedMessage id="checkout.invoice_summary.sales_tax" />}
            />
            {payments.length > 0 && invoiceTotal > 0 && (
                <>
                    <RowDivider />
                    <InvoiceSummaryListRow
                        amount={{
                            amount: invoiceTotal,
                            currency,
                        }}
                        bold
                        label={<FormattedMessage id="checkout.total" />}
                    />
                    <RowDivider />
                </>
            )}
        </InvoiceSummaryList>
    );
};

export default InvoiceSummaryCharges;

export const StyledRowDividerTop = styled(RowDivider)`
    margin-bottom: 32px;
`;
